import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ShoppingCart, ExternalLink } from 'lucide-react';
import { optimizedImageProps } from '../utils/imageUrl';
import { getProductUrlSlug } from '../data/seoConfig';
import { saleForCatNo } from '../utils/productSale';
import { SaleBadge, SalePrice, SaleCountdown, useSaleBoundaryRerender } from './ProductSale';

/**
 * Reusable product card used across the new homepage sections (Most Requested,
 * New to Catalog, Recently Restocked, etc.). Renders:
 *   - product image with descriptive alt
 *   - name + strength
 *   - price (or "Quoted on invoice")
 *   - stock-status pill
 *   - View COA link  (mailto fallback if no CoA on file)
 *   - View Product link
 *   - Add to Research Order button (calls onAddToOrder if provided)
 *
 * Visually matches the navy/blue/silver/white brand. Compact enough to fit a
 * 4-up grid on desktop and 1-up on mobile.
 */
const HomeProductCard = ({ product, onAddToOrder, testIdPrefix = 'product-card' }) => {
  useSaleBoundaryRerender(product);
  if (!product) return null;
  const sale = saleForCatNo(product, product.catNo);
  const inStock =
    product.inStock !== false &&
    (product.stock_count === undefined || product.stock_count !== 0);
  const urlSlug = getProductUrlSlug(product);
  const productLink = `/products/${urlSlug}`;
  const fullName = `${product.name}${product.dosage ? ' ' + product.dosage : ''}`;

  const coas = Array.isArray(product.coas) ? product.coas : [];
  const latestCoa = coas.length ? coas[coas.length - 1] : null;
  const coaHref = latestCoa && latestCoa.file_url
    ? latestCoa.file_url
    : `mailto:research@rcmbiosciences.com?subject=CoA%20Request%20${encodeURIComponent(product.catNo || urlSlug)}`;

  const priceLabel = product.price1 != null
    ? `$${Number(product.price1).toFixed(2)}`
    : 'Quoted on invoice';

  return (
    <div
      data-testid={`${testIdPrefix}-${product.catNo || urlSlug}`}
      className="rcm-card"
      style={{
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: '1px solid rgba(15, 46, 109, 0.1)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      <Link
        to={productLink}
        style={{
          background: 'rgba(15, 46, 109, 0.3)',
          height: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          textDecoration: 'none',
        }}
        aria-label={fullName}
      >
        {product.image ? (
          <img
            {...optimizedImageProps(product.image, { sizes: '(max-width: 640px) 45vw, 240px', maxWidth: 640 })}
            alt={`${fullName} research peptide — RCM Biosciences product photo`}
            width="200"
            height="200"
            loading="lazy"
            decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <div style={{ color: '#fff', textAlign: 'center' }}>
            <h3 style={{ fontSize: '16px', margin: 0 }}>{product.name}</h3>
            <p style={{ fontSize: '12px', opacity: 0.85, marginTop: '4px' }}>{product.dosage}</p>
          </div>
        )}
      </Link>

      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', flex: 1, gap: '6px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--brand-primary)', margin: 0, lineHeight: 1.3 }}>
          <Link to={productLink} style={{ color: 'inherit', textDecoration: 'none' }}>
            {product.name}
          </Link>
        </h3>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {product.dosage || 'Research Peptide'}
        </p>
        {sale.active && (
          <div className="flex items-center gap-2 flex-wrap" data-testid={`${testIdPrefix}-sale`}>
            <SaleBadge testid={`${testIdPrefix}-sale-badge`} />
            <SaleCountdown endsAt={sale.endsAt} compact testid={`${testIdPrefix}-sale-countdown`} />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
          {sale.active ? (
            <SalePrice regular={sale.regular} sale={sale.salePrice} testid={`${testIdPrefix}-price`} />
          ) : (
            <span data-testid={`${testIdPrefix}-price`} style={{ fontSize: '16px', fontWeight: 700, color: 'var(--brand-primary)' }}>
              {priceLabel}
            </span>
          )}
          <span
            data-testid={`${testIdPrefix}-stock`}
            style={{
              fontSize: '10px',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: '10px',
              background: inStock ? 'rgba(16,185,129,0.10)' : 'rgba(156,163,175,0.15)',
              color: inStock ? '#059669' : '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {inStock ? 'In stock' : 'Out of stock'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '6px', marginTop: 'auto', paddingTop: '10px', flexWrap: 'wrap' }}>
          <a
            href={coaHref}
            target={coaHref.startsWith('http') || coaHref.startsWith('/api') ? '_blank' : undefined}
            rel="noopener noreferrer"
            data-testid={`${testIdPrefix}-view-coa`}
            style={{
              flex: '1 1 80px',
              fontSize: '11px',
              padding: '7px 8px',
              borderRadius: '4px',
              border: '1px solid rgba(15,46,109,0.25)',
              color: 'var(--brand-primary)',
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
            }}
          >
            <FileText size={11} /> COA
          </a>
          <Link
            to={productLink}
            data-testid={`${testIdPrefix}-view-product`}
            style={{
              flex: '1 1 80px',
              fontSize: '11px',
              padding: '7px 8px',
              borderRadius: '4px',
              border: '1px solid rgba(15,46,109,0.25)',
              color: 'var(--brand-primary)',
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
            }}
          >
            <ExternalLink size={11} /> View
          </Link>
        </div>

        <button
          type="button"
          onClick={() => onAddToOrder && onAddToOrder(product)}
          disabled={!inStock || !onAddToOrder}
          data-testid={`${testIdPrefix}-add`}
          style={{
            marginTop: '6px',
            background: inStock ? 'var(--brand-primary)' : '#9ca3af',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '9px 12px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: inStock && onAddToOrder ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            letterSpacing: '0.03em',
          }}
        >
          <ShoppingCart size={12} /> Add to Research Order
        </button>
      </div>
    </div>
  );
};

export default HomeProductCard;
